import { sessionService, SessionService } from './SessionService'
import type {
  CreateSessionInput,
  SessionRecord,
  SessionValidationResult,
  UpdateSessionInput,
} from './SessionTypes'

/**
 * SessionManager — façade every connector must use for login/session lifecycle.
 * Real browser login (Playwright) will plug in here later; Sprint 2.3 is infrastructure only.
 *
 * Extension points (future):
 * - attachBrowserContext(sessionId, playwrightContext)
 * - syncStorageState(sessionId)
 * - warmSession(integrationId) via headless login workers
 */
export class SessionManager {
  private readonly service: SessionService

  constructor(service: SessionService = sessionService) {
    this.service = service
  }

  createSession(input: CreateSessionInput): SessionRecord {
    return this.service.createSession(input)
  }

  updateSession(id: string, patch: UpdateSessionInput): SessionRecord | null {
    return this.service.updateSession(id, patch)
  }

  getSession(id: string): SessionRecord | null {
    return this.service.getSession(id)
  }

  getActiveSession(integrationId: string): SessionRecord | null {
    return this.service.getActiveSession(integrationId)
  }

  listSessions(): SessionRecord[] {
    return this.service.list()
  }

  listByIntegration(integrationId: string): SessionRecord[] {
    return this.service.listByIntegration(integrationId)
  }

  validateSession(id: string): SessionValidationResult {
    return this.service.validateSession(id)
  }

  invalidateSession(id: string, reason?: string): SessionRecord | null {
    return this.service.invalidateSession(id, reason)
  }

  removeSession(id: string): boolean {
    return this.service.removeSession(id)
  }

  /**
   * Placeholder for future Playwright bootstrap.
   * Returns a plan object; does not launch browsers in Sprint 2.3.
   */
  preparePlaywrightSession(integrationId: string): {
    ok: true
    placeholder: true
    integrationId: string
    message: string
  } {
    return {
      ok: true,
      placeholder: true,
      integrationId,
      message:
        'Playwright session bootstrap reserved. Use SessionManager.createSession() for now; no browser launch.',
    }
  }
}

export const sessionManager = new SessionManager()
